import supabase from '../config/supabase';
// The same direct-order leg the server's bucket builder runs
// (purchase-order-dispatch → planCountOrderLine): the preview must show the
// packages, the price and the vendor the created draft will actually carry,
// or the hub lies. Both sides import THESE modules — there is no second copy
// of the resolution to drift.
import { planCountOrderLine } from '../../../supabase/functions/_shared/countOrderLine.mjs';
import { resolveDirectOrderOption } from '../../../supabase/functions/_shared/orderOptionSelection.mjs';
import {
  buildEquivalentGroups,
  expandOptionsAcrossEquivalentGroups,
} from '../../../supabase/functions/_shared/shoppingCandidates.mjs';
import { resolvePoLinePackSize } from '../utils/poLinePack';

// Mirrors the SQL `normalize_vendor_name` / the dispatch bucket builder's
// normalizer, so a name-only vendor snapshot resolves to the SAME live vendor
// the server will resolve — the preview and the created draft must agree on
// which lines get the vendor's package terms.
const normalizeVendorNameForLookup = (value) => String(value ?? '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const isValidEmailForOrders = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim());

// Mirrors purchase-order-dispatch#buildContactFromVendorRow. A preview that
// judges "can this be sent?" by a different recipient rule than the server
// uses is the same class of lie as a preview that shows a different price.
const contactFromVendorRow = (vendor) => {
  const v = vendor || {};
  const contactEmail = String(v.contact_email || '').trim();
  const generalEmail = String(v.general_email || '').trim();
  const sendToContact = v.send_orders_to_contact_email;
  const sendToGeneral = v.send_orders_to_general_email;
  const flagsExplicit = sendToContact !== undefined && sendToContact !== null
    && sendToGeneral !== undefined && sendToGeneral !== null;

  const recipients = [];
  if (flagsExplicit) {
    if (sendToContact && isValidEmailForOrders(contactEmail)) recipients.push(contactEmail);
    if (sendToGeneral && isValidEmailForOrders(generalEmail)) recipients.push(generalEmail);
  } else if (isValidEmailForOrders(contactEmail)) {
    recipients.push(contactEmail);
  } else if (isValidEmailForOrders(generalEmail)) {
    recipients.push(generalEmail);
  }
  const seen = new Set();
  return {
    contactName: String(v.contact_name || '').trim(),
    contactEmail,
    contactPhone: String(v.contact_phone || '').trim() || String(v.general_phone || '').trim(),
    generalEmail,
    orderRecipientEmails: recipients.filter((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
  };
};

/**
 * Fetch count items for a completed count session and build the order report
 * vendor structure (same shape as the post-count OrderReviewOverlay uses).
 */
export async function fetchSessionOrderReport(sessionId) {
  if (!sessionId) return [];

  const BASE_COLUMNS = 'id, inventory_item_id, location, is_counted, current_count, actual_quantity, '
    + 'calculated_order_quantity, manual_order_quantity, par_level, expected_quantity, '
    + 'selected_purchase_option_id, unit_price_snapshot, item_snapshot, snapshot_at_close';
  // par_normalized_* arrives with migration 20260730120000 and
  // normalized_unit_cost_snapshot with 20260724120004. Ask for them, and fall
  // back to the columns that have always existed if they are not deployed —
  // the report is unchanged in that case rather than failing outright.
  const run = (columns) => supabase
    .from('count_items')
    .select(columns)
    .eq('count_session_id', sessionId)
    .order('id', { ascending: true });

  const OPTIONAL = 'par_normalized_quantity, par_normalized_unit, normalized_unit_cost_snapshot';
  let { data: items, error } = await run(`${BASE_COLUMNS}, ${OPTIONAL}`);
  if (error && /column .* does not exist|schema cache/i.test(String(error.message || ''))) {
    ({ data: items, error } = await run(BASE_COLUMNS));
  }

  if (error) throw new Error(error.message);
  if (!items || items.length === 0) return [];

  // Group rows by inventory_item_id, preferring manual_order_quantity from any
  // sibling over PAR-calculated quantities. manual_order_quantity = 0 is a
  // valid override that suppresses the order line (finalQty = 0 → no line).
  // Uncounted items without any manual override are excluded.
  const eligibleRows = items.filter((r) => r.is_counted || (Number.isFinite(parseFloat(r.manual_order_quantity))));

  const invIdAgg = new Map(); // invId → { repRow, finalOrderQty, hasManualOverride, selectedOptionId }
  eligibleRows.forEach((row) => {
    const invId = row.inventory_item_id;
    const manualQty = parseFloat(row.manual_order_quantity);
    const hasManual = Number.isFinite(manualQty);
    const calcQty = parseFloat(row.calculated_order_quantity ?? 0) || 0;
    const finalOrderQty = hasManual ? manualQty : calcQty;
    const rowSelected = row.selected_purchase_option_id != null ? String(row.selected_purchase_option_id) : null;

    const key = invId || `__no_invid_${row.id}`;
    if (!invIdAgg.has(key)) {
      invIdAgg.set(key, { repRow: row, finalOrderQty, hasManualOverride: hasManual, selectedOptionId: rowSelected });
    } else {
      const agg = invIdAgg.get(key);
      if (hasManual && !agg.hasManualOverride) {
        agg.repRow = row;
        agg.finalOrderQty = manualQty;
        agg.hasManualOverride = true;
      }
      if (!agg.selectedOptionId && rowSelected) agg.selectedOptionId = rowSelected;
    }
  });

  const { data: sessionRow } = await supabase
    .from('count_sessions')
    .select('store_id')
    .eq('id', sessionId)
    .maybeSingle();
  const storeId = sessionRow?.store_id ?? null;

  const countedItemIds = [...new Set(
    [...invIdAgg.values()].map((entry) => entry.repRow.inventory_item_id).filter(Boolean),
  )];

  // Every vendor product these items could be bought as — NOT only the ones a
  // row explicitly chose. `selected_purchase_option_id` is NULL until somebody
  // picks, and NULL means "resolve the purchasing choice at read time"; a
  // preview that reads it as "no vendor product" shows the same unpriced,
  // fractional lines the draft used to carry.
  const optionsByItemId = {};
  const itemsById = new Map();
  let pooledOptionsByItemId = optionsByItemId;
  if (storeId && countedItemIds.length) {
    // A full count can carry hundreds of items, and `.in(...)` becomes a URL.
    // Chunked so a large session cannot silently truncate or fail on length —
    // the same chunk size the server's bucket builder uses.
    const ID_CHUNK = 150;
    const chunked = (values) => {
      const out = [];
      for (let i = 0; i < values.length; i += ID_CHUNK) out.push(values.slice(i, i + ID_CHUNK));
      return out;
    };
    const loadOptions = async (ids) => {
      for (const chunk of chunked(ids)) {
        const { data, error: optionErr } = await supabase
          .from('purchase_options')
          // The vendor embed is what vendorIsRetired reads; without it a
          // retired vendor's product would re-enter a NEW-order path.
          .select('*, vendor:vendors(status, is_active, deleted_at)')
          .eq('store_id', storeId)
          .in('inventory_item_id', chunk);
        if (optionErr) continue;
        (data || []).forEach((option) => {
          const key = String(option.inventory_item_id);
          (optionsByItemId[key] ||= []).push(option);
        });
      }
    };
    const loadItems = async (ids) => {
      for (const chunk of chunked(ids)) {
        const { data, error: itemErr } = await supabase
          .from('inventory_items')
          .select('*')
          .eq('store_id', storeId)
          .in('id', chunk);
        if (itemErr) continue;
        (data || []).forEach((row) => itemsById.set(String(row.id), row));
      }
    };

    try {
      await Promise.all([loadOptions(countedItemIds), loadItems(countedItemIds)]);
    } catch (_) { /* legacy shape stands */ }

    // A related-item group is ONE purchasing need, so its members' options are
    // one pool — the same expansion the count card and the Savings Review use.
    try {
      const { data: relationshipRows } = await supabase
        .from('item_relationships')
        .select('item_a_id, item_b_id, relationship_type, status')
        .eq('store_id', storeId)
        .eq('relationship_type', 'same_item')
        .eq('status', 'confirmed');
      if (relationshipRows?.length) {
        const groups = buildEquivalentGroups(relationshipRows);
        const siblingIds = [...new Set(
          countedItemIds
            .flatMap((id) => groups.membersByGroupId.get(groups.groupIdByItemId.get(String(id)) ?? '') ?? [])
            .filter((id) => !optionsByItemId[String(id)]),
        )];
        if (siblingIds.length) await Promise.all([loadOptions(siblingIds), loadItems(siblingIds)]);
        pooledOptionsByItemId = expandOptionsAcrossEquivalentGroups(optionsByItemId, groups);
      }
    } catch (_) { /* ungrouped items preview exactly as before */ }
  }

  // Live vendor directory — the server's bucket builder resolves a resolved
  // option's vendor id (and a snapshot vendor NAME) to a live vendor before
  // deciding which order a line belongs on; without the same lookup here the
  // preview and the draft would bucket differently.
  const vendorByNormName = new Map();
  const vendorById = new Map();
  if (storeId) {
    try {
      const { data: vendorRows, error: vendorErr } = await supabase
        .from('vendors')
        .select('id, normalized_display_name, display_name, contact_name, contact_email, contact_phone, general_email, general_phone, send_orders_to_contact_email, send_orders_to_general_email, status')
        .eq('store_id', storeId);
      if (!vendorErr) {
        (vendorRows || []).forEach((vendor) => {
          if (String(vendor.status || '').toLowerCase() === 'merged') return;
          vendorById.set(String(vendor.id), vendor);
          const key = (vendor.normalized_display_name && vendor.normalized_display_name.trim())
            || normalizeVendorNameForLookup(vendor.display_name);
          if (key && !vendorByNormName.has(key)) vendorByNormName.set(key, vendor);
        });
      }
    } catch (_) { /* name-only lines keep their legacy shape */ }
  }

  const buckets = new Map();

  invIdAgg.forEach(({ repRow: row, finalOrderQty: orderQty, selectedOptionId }) => {
    if (orderQty <= 0) return;

    const snap = (row.snapshot_at_close && typeof row.snapshot_at_close === 'object')
      ? row.snapshot_at_close
      : (row.item_snapshot || {});

    // Explicit choice → automatic lowest trustworthy comparable price →
    // preferred vendor → the only eligible option → nothing. One resolver,
    // shared with the server and with the vendor picker.
    const itemKey = row.inventory_item_id != null ? String(row.inventory_item_id) : null;
    const itemOptions = itemKey ? (pooledOptionsByItemId[itemKey] ?? []) : [];
    const ownItem = itemKey ? (itemsById.get(itemKey) ?? null) : null;
    const optionResolution = itemOptions.length
      ? resolveDirectOrderOption(itemOptions, selectedOptionId, { item: ownItem })
      : null;
    const resolvedOption = optionResolution?.option ?? null;
    // A product whose VENDOR this store cannot see is not orderable: there is
    // nobody to send the order to, and its package terms must not land on
    // whatever other bucket the line falls into.
    const optionVendorId = resolvedOption?.vendor_id != null ? String(resolvedOption.vendor_id) : null;
    const optionVendor = optionVendorId ? (vendorById.get(optionVendorId) || null) : null;
    const usableOption = optionVendor ? resolvedOption : null;

    // Planned BEFORE the vendor is chosen, because only a plan that actually
    // used the option may move the line to that option's vendor — the same
    // order the server's bucket builder works in.
    const plan = planCountOrderLine({
      orderQuantity: orderQty,
      snapshot: snap,
      itemSnapshot: row.item_snapshot || null,
      unitPriceSnapshot: row.unit_price_snapshot,
      normalizedUnitCostSnapshot: row.normalized_unit_cost_snapshot,
      option: usableOption,
      optionSelectionReason: optionResolution?.reason ?? null,
      packSizeFallback: resolvePoLinePackSize(snap),
    });

    const snapVendorName = String(snap.vendor_name || snap.vendor_display_snapshot || snap.vendor || '').trim();
    const snapVendorId = snap.vendor_id != null ? String(snap.vendor_id) : '';

    // The vendor PRODUCT the line was written against decides which order it
    // belongs on; the count snapshot's vendor answers otherwise.
    let liveVendor = plan.usedOption ? optionVendor : null;
    let vendorId = liveVendor ? String(liveVendor.id) : null;
    let vendorName = liveVendor ? String(liveVendor.display_name || '').trim() : '';
    if (!vendorId) {
      if (snapVendorId) {
        vendorId = snapVendorId;
        liveVendor = vendorById.get(snapVendorId) || null;
      } else if (snapVendorName) {
        liveVendor = vendorByNormName.get(normalizeVendorNameForLookup(snapVendorName)) || null;
        vendorId = liveVendor ? String(liveVendor.id) : null;
      }
      vendorName = snapVendorName || (liveVendor ? String(liveVendor.display_name || '').trim() : '');
    }
    if (!vendorName && liveVendor) vendorName = String(liveVendor.display_name || '').trim();

    const hasVendor = Boolean(vendorName);
    const displayVendorName = hasVendor ? vendorName : 'No vendor assigned';
    const key = hasVendor ? (vendorId || vendorName.toLowerCase()) : '__no_vendor__';

    const contact = liveVendor
      ? contactFromVendorRow(liveVendor)
      : {
        contactName: snap.contact_name || snap.vendor_contact_name || '',
        contactEmail: snap.contact_email || snap.vendor_contact_email || snap.email || '',
        contactPhone: snap.contact_phone || snap.vendor_contact_phone || '',
        generalEmail: snap.vendor_general_email || '',
        orderRecipientEmails: snap.order_recipient_emails || [],
      };

    if (!buckets.has(key)) {
      const canSend = hasVendor && Boolean(contact.contactEmail || contact.generalEmail || (contact.orderRecipientEmails || []).length > 0);
      const sendReadinessReason = !hasVendor
        ? 'No vendor assigned. Use Items to assign vendors.'
        : !canSend
          ? 'Vendor has no contact email. Add an email or mark as sent outside Countertop.'
          : '';
      buckets.set(key, {
        vendorId: vendorId || null,
        vendorName: displayVendorName,
        contact,
        hasAssignedVendor: hasVendor,
        canSend,
        sendReadinessReason,
        items: [],
        totalOrderValue: 0,
        unpricedCount: 0,
      });
    }

    const quantity = parseFloat(row.actual_quantity ?? row.current_count ?? 0) || 0;
    // A PAR normalized against a vendor package wins over the legacy column:
    // par_level might read "2" (tubs) while the count is in gallons, and
    // subtracting one from the other is how an order for 8 of something
    // appears out of nowhere. Legacy rows have no normalized value and keep
    // using par_level exactly as before.
    const parLevel = parseFloat(
      row.par_normalized_quantity ?? row.par_level ?? row.expected_quantity ?? snap.par_level ?? 0,
    ) || 0;
    const shortage = Math.max(0, parLevel - quantity);
    const bucket = buckets.get(key);

    bucket.items.push({
      description: snap.description || snap.name || 'Unnamed Item',
      brand: snap.brand || '',
      location: row.location || snap.location || '-',
      // On Hand and PAR are in the item's COUNT unit; the order is in the
      // vendor's ORDER unit. They are two different units on one row, so each
      // number carries its own — "12 case on hand" for 12 gallons is how a
      // reviewer approves an order for twelve times what was meant.
      unit: plan.unit || snap.unit || snap.par_unit || '',
      countUnit: snap.unit || snap.par_unit || '',
      orderUnit: plan.unit || snap.unit || snap.par_unit || '',
      packSize: plan.packSize,
      quantity,
      parLevel,
      shortage,
      orderQuantity: plan.orderQuantity,
      // NULL is PRICE UNAVAILABLE and is deliberately not 0 — see
      // countOrderLine.mjs. Surfaces render the difference.
      unitPrice: plan.unitPrice,
      orderValue: plan.lineTotal,
      priceAvailable: plan.priceAvailable,
      priceReason: plan.priceReason,
      priceSource: plan.priceSource,
      purchaseOptionId: plan.purchaseOptionId,
      optionSelectionReason: plan.optionSelectionReason,
      orderRuleAdjusted: plan.quantityAdjusted,
      orderRuleDriver: plan.quantityAdjustmentReason,
      quantityReceived: plan.quantityReceived,
    });
    bucket.totalOrderValue += plan.lineTotal ?? 0;
    if (!plan.priceAvailable) bucket.unpricedCount += 1;
  });

  return [...buckets.values()].sort((a, b) => {
    if (a.hasAssignedVendor !== b.hasAssignedVendor) return a.hasAssignedVendor ? -1 : 1;
    return (a.vendorName || '').localeCompare(b.vendorName || '');
  });
}

const HUB_ACTIONS = ['hub_send', 'hub_generate_po', 'hub_print', 'hub_cancel', 'hub_edit'];

/**
 * Fetch the most-recent Ordering Hub action recorded for each of the given
 * count session ids. Returns a map keyed by session id.
 */
export async function fetchSessionHubStatuses(sessionIds) {
  if (!sessionIds || sessionIds.length === 0) return {};

  const { data, error } = await supabase
    .from('purchase_order_activity_log')
    .select('source_count_session_id, action, created_at')
    .in('source_count_session_id', sessionIds)
    .in('action', HUB_ACTIONS)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  const map = {};
  (data || []).forEach((row) => {
    const sid = row.source_count_session_id;
    if (!sid) return;
    if (!map[sid]) {
      map[sid] = {
        lastAction: row.action,
        lastAt: row.created_at,
        hasSend: false,
        hasGeneratePo: false,
      };
    }
    if (row.action === 'hub_send') map[sid].hasSend = true;
    if (row.action === 'hub_generate_po') map[sid].hasGeneratePo = true;
  });
  return map;
}

/**
 * Record an Ordering Hub action in the audit log.
 */
export async function recordOrderingHubEvent({
  action,
  countSessionId,
  purchaseOrderId = null,
  actorEmail = '',
  details = {},
}) {
  const { data: { user } } = await supabase.auth.getUser();
  const actorUserId = user?.id || null;

  const { error } = await supabase
    .from('purchase_order_activity_log')
    .insert({
      action,
      purchase_order_id: purchaseOrderId || null,
      source_count_session_id: countSessionId || null,
      actor_user_id: actorUserId,
      actor_email: actorEmail || user?.email || '',
      details: { ...details, hub: true },
    });

  if (error) {
    console.warn('[OrderingHub] Failed to record event', action, error.message);
    return { success: false, error };
  }
  return { success: true };
}

// ---------------------------------------------------------------------------
// Work-queue hub query helpers
// ---------------------------------------------------------------------------

const PO_SUMMARY_FIELDS = [
  'id', 'po_number', 'store_id', 'vendor_id', 'vendor_name_snapshot',
  'vendor_contact_snapshot', 'ordered_by_name', 'ordered_by_email', 'ordered_by_user_id',
  'source_type', 'notes', 'subtotal', 'total', 'line_count',
  'delivery_status', 'sent_at', 'created_at', 'updated_at',
  'parent_po_id', 'revision_number', 'is_latest_revision', 'revision_summary',
  'requested_delivery_date', 'delivery_notes',
  'resend_message_id', 'opened_at', 'delivered_at', 'clicked_at', 'bounced_at', 'complained_at', 'delayed_at',
  'archived_at', 'canceled_at', 'voided_at', 'replaced_at',
  'replacement_for_po_id', 'replaced_by_po_id', 'cancellation_reason', 'void_reason', 'contact_updated_from',
  // Embedded joins for linked PO numbers shown in ReplacementBanner.
  // Use FK *column name* instead of constraint name — more resilient to
  // PostgREST schema-cache lag after the lifecycle migration.
  'replaced_by_po:purchase_orders!replaced_by_po_id(po_number)',
  'replacement_for_po:purchase_orders!replacement_for_po_id(po_number)',
].join(', ');

/**
 * List all draft purchase orders for a store (non-amendment drafts first).
 * Stale threshold: 48 hours.
 */
export async function listDraftOrders(storeId) {
  if (!storeId) return [];
  const { data, error } = await supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('store_id', storeId)
    .eq('delivery_status', 'draft')
    .eq('revision_number', 0)
    .is('archived_at', null)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * List the latest sent POs (not superseded revisions).
 * Sent statuses: sent, delivered, opened, clicked, pending_approval, sending, blocked_no_email, bounced, error, delivery_delayed.
 */
export async function listLatestSentPOs(storeId, opts = {}) {
  if (!storeId) return [];
  const limit = Math.min(opts.limit || 50, 100);
  const SENT_STATUSES = ['sent', 'delivered', 'opened', 'clicked', 'sending', 'pending_approval', 'blocked_no_email', 'bounced', 'error', 'confirmed', 'delivery_delayed'];
  const { data, error } = await supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('store_id', storeId)
    .in('delivery_status', SENT_STATUSES)
    .eq('is_latest_revision', true)
    .is('archived_at', null)
    .order('sent_at', { ascending: false, nullsLast: true })
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * List amendment drafts (draft POs with revision_number > 0).
 */
export async function listAmendmentDrafts(storeId) {
  if (!storeId) return [];
  const { data, error } = await supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('store_id', storeId)
    .eq('delivery_status', 'draft')
    .gt('revision_number', 0)
    .is('archived_at', null)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * List order history (all non-draft, non-latest, or older sent POs).
 */
export async function listOrderHistory(storeId, filters = {}) {
  if (!storeId) return [];
  const limit = Math.min(filters.limit || 30, 100);
  const HISTORY_STATUSES = ['sent', 'delivered', 'opened', 'clicked', 'bounced', 'error', 'blocked', 'blocked_no_email', 'revised', 'complained'];
  let query = supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('store_id', storeId)
    .in('delivery_status', HISTORY_STATUSES)
    .is('archived_at', null)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (filters.vendorId) query = query.eq('vendor_id', filters.vendorId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * List received and closed purchase orders (non-archived by default).
 */
export async function listReceivedPOs(storeId, { includeArchived = false } = {}) {
  if (!storeId) return [];
  let query = supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('store_id', storeId)
    .in('delivery_status', ['received', 'closed'])
    .order('created_at', { ascending: false })
    .limit(60);
  if (!includeArchived) query = query.is('archived_at', null);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * List canceled, replaced, and voided purchase orders (non-archived by default).
 */
export async function listCanceledVoidedPOs(storeId, { includeArchived = false } = {}) {
  if (!storeId) return [];
  let query = supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('store_id', storeId)
    .in('delivery_status', ['canceled', 'replaced', 'voided'])
    .order('canceled_at', { ascending: false, nullsLast: true })
    .order('created_at', { ascending: false })
    .limit(60);
  if (!includeArchived) query = query.is('archived_at', null);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * List all archived purchase orders for a store.
 */
export async function listArchivedPOs(storeId) {
  if (!storeId) return [];
  const { data, error } = await supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('store_id', storeId)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Fetch the most-recent 'draft_create_failed' event recorded for a count
 * session. Returns { error, occurredAt, details } or null if no failure has
 * been logged. Used by the Ordering Hub's CountOrderReview to surface the
 * underlying reason next to the "Create Draft Order" retry button.
 */
export async function fetchLastDraftCreateFailure(sessionId) {
  if (!sessionId) return null;
  const { data, error } = await supabase
    .from('purchase_order_activity_log')
    .select('action, details, created_at')
    .eq('source_count_session_id', sessionId)
    .eq('action', 'draft_create_failed')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.warn('[OrderingHub] fetchLastDraftCreateFailure failed', error.message);
    return null;
  }
  const row = (data || [])[0];
  if (!row) return null;
  const details = (row.details && typeof row.details === 'object') ? row.details : {};
  const message = typeof details.error === 'string' && details.error.trim()
    ? details.error.trim()
    : 'Unknown error';
  return {
    error: message,
    occurredAt: row.created_at,
    details,
  };
}

/**
 * List purchase orders (any status) that originated from the given count
 * session. Used by the Ordering Hub's CountOrderReview to detect when a
 * completed count has no associated draft PO and offer a retry button.
 */
export async function listPurchaseOrdersForCountSession(sessionId) {
  if (!sessionId) return [];
  const { data, error } = await supabase
    .from('purchase_orders')
    .select(PO_SUMMARY_FIELDS)
    .eq('source_count_session_id', sessionId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Unified paginated fetch for the new Ordering hub landing.
 * Returns all POs for a store most-recent-first, with optional status filter
 * and server-side search across PO-level fields AND line-item descriptions/locations.
 *
 * Each returned PO includes a `purchase_order_items` array (description, location,
 * sort_order) for use by client-side category sorts.
 *
 * Search behaviour:
 *   - When search is blank: normal pagination (page × pageSize).
 *   - When search is active: runs two parallel queries (PO-level OR filter +
 *     purchase_order_items OR filter), merges the deduped PO sets, then applies
 *     status filter via a follow-up .in('id', matchedIds) fetch.  Pagination is
 *     disabled (result capped at 500) because the merged set must be complete before
 *     the caller can sort/display.
 */
const SENT_STATUSES_HUB = [
  'sent', 'delivered', 'opened', 'clicked', 'sending',
  'pending_approval', 'blocked_no_email', 'bounced', 'error', 'confirmed', 'blocked', 'delivery_delayed',
];

// PO fields + embedded line-items (description + location for search/sort)
const PO_HUB_FIELDS = PO_SUMMARY_FIELDS + ', purchase_order_items!left(id, description, location, sort_order)';

function applyStatusFilter(query, statusFilter) {
  if (statusFilter === 'drafts') {
    return query.eq('delivery_status', 'draft').is('archived_at', null);
  } else if (statusFilter === 'sent') {
    return query.in('delivery_status', SENT_STATUSES_HUB).is('archived_at', null);
  } else if (statusFilter === 'received') {
    return query.in('delivery_status', ['received', 'closed']).is('archived_at', null);
  } else if (statusFilter === 'canceled') {
    return query.in('delivery_status', ['canceled', 'replaced', 'voided']).is('archived_at', null);
  } else if (statusFilter === 'archived') {
    return query.not('archived_at', 'is', null);
  }
  // 'all' → no filter, include archived and non-archived
  return query;
}

export async function listAllPOsForHub(storeId, {
  page = 0,
  pageSize = 50,
  statusFilter = 'all',
  search = '',
} = {}) {
  if (!storeId) return { data: [], hasMore: false };

  const from = page * pageSize;
  const to = from + pageSize - 1;
  const esc = search ? String(search).trim().replace(/[%_]/g, (m) => `\\${m}`) : '';

  // When no search term: simple paginated query.
  if (!esc) {
    let query = supabase
      .from('purchase_orders')
      .select(PO_HUB_FIELDS)
      .eq('store_id', storeId)
      .order('created_at', { ascending: false })
      .range(from, to);
    query = applyStatusFilter(query, statusFilter);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    const rows = data || [];
    return { data: rows, hasMore: rows.length === pageSize };
  }

  // When search is active: build a combined OR filter that covers both PO-level
  // fields and any PO whose items (description/location) match — all within this store.
  //
  // Strategy:
  //  1. Fetch matching PO IDs via store-scoped item join (PostgREST !inner join keeps
  //     results confined to the correct store; no cross-store leakage).
  //  2. Append those IDs to the PO-level OR expression.
  //  3. Run a single paginated query — hasMore works correctly.

  let itemMatchedPoIds = [];
  try {
    // purchase_orders!inner join scopes the item search to this store only —
    // the outer .eq('store_id', storeId) ensures no cross-store leakage.
    //
    // Fields searched: description and location.
    // P&L-category search (derived from inventory subcategory → pnl_category_mappings)
    // is not stored on purchase_order_items rows and therefore cannot be queried here.
    // That gap is tracked in follow-up #503 and will require a server-side RPC or a
    // denormalised pnl_category snapshot column on purchase_order_items.
    //
    // The .limit(1000) cap is intentionally generous: each store rarely has more than
    // a few hundred items whose description or location matches a short search term.
    // Stores with item counts above this threshold will experience silently truncated
    // item-match results (PO-level matches always remain complete).
    const { data: idRows } = await supabase
      .from('purchase_orders')
      .select('id, purchase_order_items!inner(id)')
      .eq('store_id', storeId)
      .or(`description.ilike.%${esc}%,location.ilike.%${esc}%`, { foreignTable: 'purchase_order_items' })
      .limit(1000);
    itemMatchedPoIds = [...new Set((idRows || []).map((r) => r.id).filter(Boolean))];
  } catch {
    // Non-critical — fall back to PO-level search only if item join fails.
  }

  // Build the combined OR clause
  const orClauses = [
    `po_number.ilike.%${esc}%`,
    `vendor_name_snapshot.ilike.%${esc}%`,
    `ordered_by_name.ilike.%${esc}%`,
    `ordered_by_email.ilike.%${esc}%`,
    `notes.ilike.%${esc}%`,
    `cancellation_reason.ilike.%${esc}%`,
    `void_reason.ilike.%${esc}%`,
  ];
  if (itemMatchedPoIds.length > 0) {
    // PostgREST supports id.in.(a,b,c) inside .or()
    orClauses.push(`id.in.(${itemMatchedPoIds.join(',')})`);
  }

  let query = supabase
    .from('purchase_orders')
    .select(PO_HUB_FIELDS)
    .eq('store_id', storeId)
    .or(orClauses.join(','))
    .order('created_at', { ascending: false })
    .range(from, to);
  query = applyStatusFilter(query, statusFilter);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  const rows = data || [];
  return { data: rows, hasMore: rows.length === pageSize };
}

/**
 * Batch-fetch draft purchase orders for a set of count session ids.
 * Returns a map keyed by source_count_session_id → array of draft PO rows.
 * Only drafts (delivery_status = 'draft', not archived) are included.
 */
export async function listDraftPOsForSessions(sessionIds, storeId = null) {
  if (!sessionIds || sessionIds.length === 0) return {};
  let query = supabase
    .from('purchase_orders')
    .select('id, po_number, store_id, vendor_id, vendor_name_snapshot, vendor_contact_snapshot, source_count_session_id, delivery_status, created_at, total')
    .in('source_count_session_id', sessionIds)
    .eq('delivery_status', 'draft')
    .is('archived_at', null)
    .order('created_at', { ascending: true });
  if (storeId) query = query.eq('store_id', storeId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  const map = {};
  (data || []).forEach((po) => {
    const sid = po.source_count_session_id;
    if (!sid) return;
    if (!map[sid]) map[sid] = [];
    map[sid].push(po);
  });
  return map;
}

/**
 * Compute elapsed Mon–Fri hours between startDate and endDate (default: now).
 * Saturday and Sunday are skipped entirely so a PO sent on Friday does not
 * accumulate "unopened" hours over the weekend.
 *
 * Examples (business-hour interpretation):
 *   Fri 3pm → Mon 3pm  = 24 business hours
 *   Sat 3pm → Mon 3pm  =  0 business hours  (clock starts at Mon 3pm)
 *   Sat 3pm → Tue 3pm  = 24 business hours  (Mon 3pm + 24h = Tue 3pm)
 */
export function businessHoursElapsed(startDate, endDate = new Date()) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return 0;

  // If the send happened on a weekend, advance to Monday at the SAME time-of-day.
  // Example: Sat 3 pm send → treated as Mon 3 pm, so 24 business hours = Tue 3 pm
  // (not Tue 00:00, which is what midnight-snap would produce).
  let cur = new Date(start);
  const startDay = cur.getDay();
  if (startDay === 6) {
    cur.setDate(cur.getDate() + 2); // Saturday → Monday (same HH:mm:ss)
  } else if (startDay === 0) {
    cur.setDate(cur.getDate() + 1); // Sunday → Monday (same HH:mm:ss)
  }
  if (cur >= end) return 0;

  let elapsed = 0;
  while (cur < end) {
    const day = cur.getDay(); // 0=Sun, 6=Sat
    if (day === 6) {
      // Mid-loop Saturday always arrives here at Sat 00:00 (from the weekday
      // stretch below), so advancing +2 days stays at midnight — no setHours needed.
      const next = new Date(cur);
      next.setDate(next.getDate() + 2);
      cur = next < end ? next : end;
      continue;
    }
    if (day === 0) {
      // Defensive: Sunday shouldn't arise from normal weekday→Sat→Mon flow,
      // but handle it the same way (advance to Monday, same time).
      const next = new Date(cur);
      next.setDate(next.getDate() + 1);
      cur = next < end ? next : end;
      continue;
    }
    // Weekday stretch: advance cur to the following Saturday 00:00 (or end).
    // Using Sat 00:00 as the boundary is correct because it is exactly the
    // first non-weekday instant — no weekday hours are missed or double-counted.
    const nextSat = new Date(cur);
    nextSat.setDate(nextSat.getDate() + (6 - day));
    nextSat.setHours(0, 0, 0, 0);
    const boundary = nextSat < end ? nextSat : end;
    elapsed += (boundary.getTime() - cur.getTime()) / 3_600_000;
    cur = boundary;
  }
  return elapsed;
}

/**
 * List ordering problems:
 * - Drafts that are stale (> 48 hours old)
 * - Bounced, errored, complained, or delivery-delayed POs
 * - Sent POs where vendor contact email appears missing (blocked_no_email)
 * - Potentially stalled POs (sent 24+ real hours ago, not yet opened) — callers
 *   should apply businessHoursElapsed() to filter to true 24 business-hour stalls.
 */
export async function listOrderingProblems(storeId) {
  if (!storeId) return { staleDrafts: [], bouncedOrFailed: [], missingEmail: [], stalledPOs: [] };
  const STALE_THRESHOLD_MS = 48 * 60 * 60 * 1000;
  const cutoff = new Date(Date.now() - STALE_THRESHOLD_MS).toISOString();
  // Conservative 24h real-time cutoff for the stalled query; business-hours
  // filtering happens client-side via businessHoursElapsed().
  const stalledCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  // Non-terminal sent statuses where "not opened" is still meaningful
  // delivery_delayed is already surfaced in the bouncedOrFailed bucket; exclude it
  // here so a delayed PO doesn't appear in both alert groups simultaneously.
  const STALL_CANDIDATE_STATUSES = ['sent', 'delivered'];

  const [draftsRes, problemsRes, stalledRes] = await Promise.all([
    supabase
      .from('purchase_orders')
      .select(PO_SUMMARY_FIELDS)
      .eq('store_id', storeId)
      .eq('delivery_status', 'draft')
      .is('archived_at', null)
      .lt('created_at', cutoff)
      .order('created_at', { ascending: true }),
    supabase
      .from('purchase_orders')
      .select(PO_SUMMARY_FIELDS)
      .eq('store_id', storeId)
      .in('delivery_status', ['bounced', 'complained', 'error', 'failed', 'blocked', 'blocked_no_email', 'delivery_delayed'])
      .eq('is_latest_revision', true)
      .is('archived_at', null)
      .order('created_at', { ascending: false })
      .limit(50),
    supabase
      .from('purchase_orders')
      .select(PO_SUMMARY_FIELDS)
      .eq('store_id', storeId)
      .in('delivery_status', STALL_CANDIDATE_STATUSES)
      .eq('is_latest_revision', true)
      .is('archived_at', null)
      .is('opened_at', null)
      .not('sent_at', 'is', null)
      .lt('sent_at', stalledCutoff)
      .order('sent_at', { ascending: false })
      .limit(50),
  ]);

  // Resend retries delayed emails for a window before giving up. Only surface
  // delivery_delayed as a hard failure once that recovery window has passed.
  const DELAY_RECOVERY_WINDOW_MS = 4 * 60 * 60 * 1000; // 4 hours
  const stale = draftsRes.data || [];
  const problems = problemsRes.data || [];
  const potentiallyStalled = stalledRes.data || [];
  return {
    staleDrafts: stale,
    bouncedOrFailed: problems.filter((p) => {
      if (p.delivery_status === 'delivery_delayed') {
        // Only show after the recovery window — within 4 h Resend may still deliver.
        const delayedAt = p.delayed_at ? new Date(p.delayed_at).getTime() : 0;
        return delayedAt > 0 && (Date.now() - delayedAt) > DELAY_RECOVERY_WINDOW_MS;
      }
      return ['bounced', 'complained', 'error', 'failed', 'blocked'].includes(p.delivery_status);
    }),
    missingEmail: problems.filter((p) => p.delivery_status === 'blocked_no_email'),
    stalledPOs: potentiallyStalled,
  };
}
